import React, { useContext, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useDebounce } from 'use-debounce/lib';
import { withStyles } from '@material-ui/core/styles';
import { Paper, CircularProgress } from '@material-ui/core';
import EnhancedTableToolbar from '../common/table/EnhancedTableToolbar';
import MetricsTable from '../common/table/MetricsTable';
import { AllMetricsStyles } from './AllMetricsStyles';
import { SelectedMetricsContext } from '../../context/SelectedMetricsContext';
import { DispatchContext, StateContext } from '../../context/AllMetricsContext';
import Error from '../common/Error';
import Track from '../../helpers/Analytics';
import {
  DATA_LAYER_TYPE_PAYWALL__NONE,
  DATA_LAYER_TYPE_UID_PREFIX_FORECASTS_PREMIUM,
  DATA_LAYER_TYPE_SECTION__FORECASTS_PREMIUM,
  getAccessTypeOnSubscriptionService,
  getUserSubscriptionTierFromCookies,
  useDataLayer
} from '@insiderintelligence/datalayer';

const AllMetrics = props => {
  const dispatch = useContext(DispatchContext);
  const { page, rowsPerPage, order, orderBy, metricNameFilter } = useContext(StateContext);
  const [debouncedMetricNameFilter] = useDebounce(metricNameFilter, 2000);
  const [selectedMetrics, setSelectedMetrics] = useContext(SelectedMetricsContext);

  // dataLayer
  const userSubscriptionTier = getUserSubscriptionTierFromCookies(typeof document !== 'undefined' ? document.cookie : '');
  useDataLayer({
    accessType: getAccessTypeOnSubscriptionService(userSubscriptionTier),
    paywallType: DATA_LAYER_TYPE_PAYWALL__NONE,
    isAdBlock: false,
    isLocked: false,
    unifiedAssetId: `${DATA_LAYER_TYPE_UID_PREFIX_FORECASTS_PREMIUM}index`,
    section: DATA_LAYER_TYPE_SECTION__FORECASTS_PREMIUM,
    userSubscriptionTier
  });

  // Force all metrics to be selected if it is not
  useEffect(() => {
    if (selectedMetrics.find(m => m.selected === false)) {
      setSelectedMetrics(
        selectedMetrics.map(metric => {
          metric.selected = true;
          metric.geographies = []; // This will reset the select field on geographies
          return metric;
        })
      );
    }
  }, [selectedMetrics, setSelectedMetrics]);

  useEffect(() => {
    Track('Pro-PremiumForecast-Homepage', null, true);
  }, []);

  const getLuceneQuery = () => {
    // eslint-disable-next-line no-useless-escape
    if (debouncedMetricNameFilter && debouncedMetricNameFilter.trim().length > 0) return `${debouncedMetricNameFilter.trim()}`;
    return '';
  };

  const { classes } = props;

  const METRICS_PAGINATE_QUERY = gql`
    query metricsPaginateQuery($luceneQuery: String, $first: Int, $offset: Int, $limitx: Int, $skipx: Int, $sort: String) {
      metricsFullTextIndexSearch(luceneQuery: $luceneQuery, first: $first, offset: $offset, limitx: $limitx, skipx: $skipx, sort: $sort) {
        count
        metrics {
          id
          name
          datapointCount
          geographies {
            geographyId
            name
          }
        }
      }
    }
  `;

  const paginateQueryResult = useQuery(METRICS_PAGINATE_QUERY, {
    variables: {
      luceneQuery: getLuceneQuery(),
      limitx: rowsPerPage,
      skipx: rowsPerPage * page,
      sort: order.toUpperCase()
    },
    onCompleted(data) {
      if (debouncedMetricNameFilter && debouncedMetricNameFilter.trim().length > 0) {
        Track('Search', { searchQuery: debouncedMetricNameFilter, metrics: data.metricsFullTextIndexSearch.metrics.map(m => m.name).join(', ') });
      }
    }
  });

  const { error, loading, data } = paginateQueryResult;
  const metrics = data && data.metricsFullTextIndexSearch.metrics;
  const totalMetricCount = data && data.metricsFullTextIndexSearch.count;

  return (
    <>
      {loading && <CircularProgress style={{ position: 'fixed' }} />}
      {error && <Error />}
      {data && (
        <Paper className={classes.AllMetrics_paper}>
          <EnhancedTableToolbar
            numSelected={selectedMetrics.filter(m => m.selected).length}
            tooltip={selectedMetrics
              .filter(m => m.selected && m.name && m.name.length > 0)
              .map(m => (
                <div key={m.id}>{m.name}</div>
              ))}
            title='Pick Metrics'
            linkTo={`/metric/${selectedMetrics
              .filter(m => m.selected)
              .map(m => `${m.id}`)
              .join(',')}`}
            linkText='Visualize selected metrics'
            onClear={() => setSelectedMetrics([])}
            showSearchField
            metricNameFilter={metricNameFilter}
            onMetricNameFilterChanged={value => {
              dispatch({ type: 'setMetricNameFilter', payload: value });
            }}
          />
          <MetricsTable
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            onMetricsSelected={setSelectedMetrics}
            totalMetricCount={totalMetricCount}
            onChangePage={(_, newPage) => dispatch({ type: 'setPage', payload: newPage })}
            onChangeRowsPerPage={event => {
              dispatch({ type: 'setRowsPerPage', payload: parseInt(event.target.value, 10) });
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangeOrder={(newOrder, newOrderBy) => {
              dispatch({ type: 'setOrder', payload: { order: newOrder, orderBy: newOrderBy } });
            }}
            order={order}
            orderBy={orderBy}
            showPager
            enableSorting
            analyticsPreName={'Homepage'}
            disableScrollArrow
            disableRowClick={selectedMetrics.length >= 10}
            showSelectedMetrics={true}
          />
        </Paper>
      )}
    </>
  );
};

export default withStyles(AllMetricsStyles)(AllMetrics);
